import { EventType } from "@/analytics/analytics-events";
import { useDataManagementContext } from "@/components/ui/data-management/data-management-context";
import { ReactComponent as EmptyProjectSvg } from "@/components/ui/icons/empty-project.svg";
import { ReactComponent as ImportDataSvg } from "@/components/ui/icons/import-data.svg";
import { useDataManagementFileHandler } from "@/hooks/use-data-management-file-handler";
import { useHasDraftInProgress } from "@/hooks/use-has-draft-in-progress";
import { usePollNewMergedRevision } from "@/hooks/use-poll-new-merged-revision";
import { EnsureWriteAccess } from "@/permissions/ensure-project-access";
import { selectIsProjectEmpty } from "@/store/selections-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  selectCanUploadWithDataManagement,
  selectCanUsePhotogrammetry,
} from "@/store/subscription-features/subscription-features-selectors";
import {
  selectCurrentUser,
  selectHasWritePermission,
} from "@/store/user-selectors";
import {
  redirectToDashboardProjectsPage,
  useNavigateToDraftView,
} from "@/utils/redirects";
import { DataManagementDropzone } from "@faro-lotv/data-management";
import {
  Banner,
  BannerProps,
  FaroButton,
  FaroSvgIcon,
  FaroText,
  FileDocumentIcon,
  FontWeights,
  grey,
  LayoutSheetSmallIcon,
  NO_TRANSLATE_CLASS,
  useBreakpointSmUp,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import {
  selectCompanyId,
  selectDashboardUrl,
  selectProjectId,
} from "@faro-lotv/project-source";
import { Grid, Stack, SxProps, Theme, useTheme } from "@mui/material";
import { useCallback, useState, type JSX } from "react";
import { CreateAreaMainBody } from "./create-area-main-body";
import { CrateAreaTitle } from "./create-area-title";
import { ImgSheetFileExtensionsWithDotsList } from "./create-area-utils";
import { ImportCard } from "./import-card";
import { ImportDroneDataCard } from "./import-drone-data-card";
import { ImportScansCard } from "./import-scans-card";
import { InformationBox } from "./information-box";
import { LimitedAccess } from "./limited-access";

/** @returns The overlay elements of this mode. */
export function CreateAreaModeOverlay(): JSX.Element | null {
  const hasWritePermission = useAppSelector(selectHasWritePermission);
  const [hasSelectedSheet, setHasSelectedSheet] = useState<boolean>(false);

  let body;
  if (hasWritePermission && hasSelectedSheet) {
    body = <SetUpProjectBody />;
  } else if (hasWritePermission) {
    body = <ImportCardsBody setHasSelectedSheet={setHasSelectedSheet} />;
  } else {
    body = <ReadOnlyBody />;
  }

  return (
    <Stack
      sx={{
        position: "relative",
        // Set the size of the element to the entire page, minus the header bar
        width: "100%",
        height: "100%",
        overflow: "auto",
      }}
    >
      <CreateAreaModeBanner />
      <Stack sx={{ flexGrow: 1 }}>{body}</Stack>
    </Stack>
  );
}

interface ImportCardsBodyProps {
  /**
   * Setter for the flag that the sheet import card has been selected or triggered by the dropzone.
   * If set to true, the sheet import page is shown.
   */
  setHasSelectedSheet: React.Dispatch<React.SetStateAction<boolean>>;
}

/** @returns Body shown to users with write access, which contains cards with import options. */
function ImportCardsBody({
  setHasSelectedSheet,
}: ImportCardsBodyProps): JSX.Element {
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const projectId = useAppSelector(selectProjectId);
  const isSmUp = useBreakpointSmUp();
  const canUpload = useAppSelector(selectCanUploadWithDataManagement);
  const canUsePhotogrammetry = useAppSelector(selectCanUsePhotogrammetry);
  const { setSheetFile } = useDataManagementContext();

  // File checking for DataManagementDropzone. The ImportScansCard has its own call.
  const {
    isCheckingFiles,
    setIsCheckingFiles,
    onFinishedCheckingFiles,
    fileTypesByCategory,
  } = useDataManagementFileHandler({
    projectId,
    from: "importScansEmpty",
    setHasSelectedSheet,
  });

  fileTypesByCategory.Sheet = ImgSheetFileExtensionsWithDotsList;

  const cardContainer = (
    <Stack
      sx={{
        gap: 2,
        pl: 2,
        pr: 2,
        pt: 4,
        alignContent: "center",
      }}
    >
      {/* Title */}
      <FaroText
        aria-label="import data title"
        variant="heading32"
        sx={{ fontWeight: FontWeights.SemiBold, textAlign: "center" }}
      >
        Bring your data into the cloud
      </FaroText>

      {/* Caption */}
      <FaroText
        aria-label="import data caption"
        variant="heading20"
        sx={{ color: grey[600], textAlign: "center" }}
      >
        Drag & drop your files or choose the type of data you want to import.
      </FaroText>

      {/* Import cards */}
      <Grid
        container
        spacing={3}
        rowSpacing={3}
        sx={{
          maxWidth: "980px",
          justifyContent: "center",
          mt: 1.5,
          // Counterbalance the white space at the top of the illustration to keep the content visually centered.
          mb: 4,
          px: 2,
        }}
      >
        {/* Card to import sheet */}
        <Grid size={isSmUp ? 4 : 12}>
          <ImportCard
            icon={
              <LayoutSheetSmallIcon sx={{ width: "40px", height: "40px" }} />
            }
            ariaLabel="import sheet"
            title="Sheet"
            description="Import project plans, floor layouts, or reference documents. Sheets help align 360 images and add context to your data."
            footer={
              <Stack
                direction="row"
                sx={{ justifyContent: "space-between", alignItems: "center" }}
              >
                <FaroText
                  aria-label="sheet extensions"
                  variant="bodyS"
                  className={NO_TRANSLATE_CLASS}
                >
                  {ImgSheetFileExtensionsWithDotsList.join(", ")}
                </FaroText>
                <FileDocumentIcon sx={{ color: grey[600] }} />
              </Stack>
            }
            onClick={() => {
              // Reset the file from a previous drag & drop action.
              setSheetFile(undefined);
              setHasSelectedSheet(true);
            }}
          />
        </Grid>

        {/* Card to import scans via the Data Management editor */}
        {projectId && canUpload && (
          <Grid size={isSmUp ? 4 : 12}>
            <ImportScansCard from="importScansEmpty" />
          </Grid>
        )}

        {/* Card to import drone data (photogrammetry photos) via the Data Management editor */}
        {projectId && canUpload && canUsePhotogrammetry && (
          <Grid size={isSmUp ? 4 : 12}>
            <ImportDroneDataCard from="importDronesEmpty" />
          </Grid>
        )}
      </Grid>
    </Stack>
  );

  return (
    <Stack
      sx={{
        alignContent: "center",
        alignItems: "center",
        gap: 2,
        height: "100%",
        mb: 4,
        justifyContent: "center",
      }}
    >
      {/* Illustration */}
      <FaroSvgIcon
        source={ImportDataSvg}
        aria-label="import data icon"
        sx={{
          width: "300px",
          height: "200px",
        }}
      />

      {/* The dropzone needs these three potentially undefined input parameters.
          If they're not available, don't show the dropzone. */}
      {currentUser && companyId && projectId ? (
        <DataManagementDropzone
          currentUser={currentUser}
          companyId={companyId}
          projectId={projectId}
          fileTypesByCategory={fileTypesByCategory}
          isChecking={isCheckingFiles}
          setIsChecking={setIsCheckingFiles}
          onFinished={onFinishedCheckingFiles}
        >
          {cardContainer}
        </DataManagementDropzone>
      ) : (
        cardContainer
      )}
    </Stack>
  );
}

/**
 * @returns Body shown to users without write access, which contains a message that the project is
 * empty and a button to go to the project overview in the Dashboard.
 */
function ReadOnlyBody(): JSX.Element {
  const dashboardUrl = useAppSelector(selectDashboardUrl);

  return (
    <Stack
      sx={{
        alignContent: "center",
        alignItems: "center",
        gap: 2,
        overflow: "scroll",
      }}
    >
      {/* Illustration */}
      <FaroSvgIcon
        source={EmptyProjectSvg}
        sx={{
          width: "300px",
          height: "200px",
        }}
      />

      {/* Title */}
      <FaroText
        variant="heading32"
        sx={{ fontWeight: FontWeights.SemiBold, textAlign: "center" }}
      >
        No data in this project
      </FaroText>

      {/* Caption */}
      <FaroText
        variant="heading20"
        sx={{ color: grey[600], textAlign: "center" }}
      >
        Once someone with edit permissions imports data, it'll show up here.
      </FaroText>

      {/* Button to go to project overview in the Dashboard */}
      <FaroButton
        sx={{
          mt: 6,
          // Counterbalance the white space at the top of the illustration to keep the content visually centered.
          mb: 4,
        }}
        onClick={() => {
          Analytics.track(EventType.openProjectOverview);
          redirectToDashboardProjectsPage(
            /* subpage */ undefined,
            dashboardUrl,
          );
        }}
      >
        Go to project overview
      </FaroButton>
    </Stack>
  );
}

/** @returns Body for setting up the project by importing a sheet. */
function SetUpProjectBody(): JSX.Element {
  const theme = useTheme();
  /** Style shared by the body of the overlay */
  const commonStyle: SxProps<Theme> = {
    backgroundColor: `${theme.palette.gray100}80`,
    width: "100%",
    py: 1.5,
    px: { xs: 2, md: 5 },
  };

  return (
    <Stack sx={{ alignSelf: "center", width: { xs: "90%", md: "80%" } }}>
      <CrateAreaTitle
        sx={{
          mt: { xs: "1.5rem", md: "60px" },
        }}
      />
      <InformationBox sx={{ ...commonStyle, mt: { xs: "1rem", md: "24px" } }} />
      <EnsureWriteAccess
        fallback={<LimitedAccess sx={{ ...commonStyle, mt: "0.625rem" }} />}
      >
        <CreateAreaMainBody sx={{ ...commonStyle, mt: "24px" }} isFirstSheet />
      </EnsureWriteAccess>
    </Stack>
  );
}

/**
 * @returns The banner to show:
 *  - Banner to reload the page if there's a newer merged revision than the one currently shown.
 *  - Banner to go to Data Management if the project is empty, the user has editing permission
 *    and there's an open draft.
 *  - null otherwise.
 */
function CreateAreaModeBanner(): JSX.Element | null {
  const projectId = useAppSelector(selectProjectId);
  const [bannerNewMerged, setBannerNewMerged] = useState<
    BannerProps | undefined
  >(undefined);

  const isProjectEmpty = useAppSelector(selectIsProjectEmpty);
  const hasDraft = useHasDraftInProgress(projectId);

  const callbackHasNewMergedRevision = useCallback((): void => {
    setBannerNewMerged({
      variant: "warning",
      title: "New version available",
      children:
        "A new version of this project has been published. Please reload the page to view it.",
      action: {
        label: "Reload",
        action: () => window.location.reload(),
      },
    });
  }, []);
  usePollNewMergedRevision(callbackHasNewMergedRevision);

  const bannerDraft = useDraftInProgressBannerProps(projectId);

  if (bannerNewMerged) {
    return <Banner {...bannerNewMerged} sx={{ m: 0 }} />;
  } else if (hasDraft && isProjectEmpty) {
    return <Banner {...bannerDraft} sx={{ m: 0 }} />;
  }
  return null;
}

/**
 * @returns The properties for the banner warning the user that there's a draft in progress
 * @param projectId The id of the current project
 */
export function useDraftInProgressBannerProps(
  projectId: GUID | undefined,
): BannerProps {
  const navigateToDraftView = useNavigateToDraftView();
  return {
    variant: "info",
    title: "Draft in progress",
    children: "This project has pending changes.",
    action: {
      label: "View draft",
      action: projectId
        ? () => navigateToDraftView(projectId, "pendingChanges")
        : undefined,
    },
  };
}
